import { type FormEvent, useState } from 'react';
import { api } from '../api';
import { PlusIcon, SearchIcon, ShieldIcon, UsersIcon } from '../components/icons';
import {
  Dialog,
  Empty,
  ErrorNote,
  formatDate,
  PageHeader,
  RowMenu,
  SkeletonRows,
  Status,
  timeAgo,
  useDebounced,
  useToast,
} from '../components/ui';
import { useLoad } from '../hooks';

interface AdminTeam {
  id: string;
  name: string;
  plan: string;
  status: 'active' | 'paused' | 'suspended';
  pausedReason: string | null;
  dailyLimit: number;
  reviewedAt: string | null;
  onboarding: {
    website: string | null;
    emailTypes: string[];
    monthlyVolume: string;
    details: string | null;
  } | null;
  createdAt: string;
  ownerEmail: string | null;
  sent: number;
  bounced: number;
  complained: number;
}

const TYPE_NAMES: Record<string, string> = {
  otp: 'OTP',
  account: 'Account',
  receipts: 'Receipts',
  notifications: 'Alerts',
  marketing: 'Marketing',
};
const VOLUME_NAMES: Record<string, string> = {
  under_1k: '<1k/mo',
  '1k_10k': '1k–10k/mo',
  '10k_100k': '10k–1L/mo',
  over_100k: '>1L/mo',
};

/** One line summarising the sign-up answers, with the free-text details on hover. */
function OnboardingLine({ team }: { team: AdminTeam }) {
  const o = team.onboarding;
  if (!o) return <span className="sub dim">Hasn't answered the sign-up questions</span>;
  return (
    <span className="sub" title={o.details ?? undefined}>
      <span style={{ color: o.emailTypes.includes('marketing') ? 'var(--warn)' : undefined }}>
        {o.emailTypes.map((t) => TYPE_NAMES[t] ?? t).join(', ')}
      </span>
      {' · '}
      {VOLUME_NAMES[o.monthlyVolume] ?? o.monthlyVolume}
      {o.website ? (
        <>
          {' · '}
          <a className="link" href={o.website} target="_blank" rel="noreferrer noopener">
            {o.website.replace(/^https?:\/\//, '')}
          </a>
        </>
      ) : null}
      {o.details ? ' · has a note' : null}
    </span>
  );
}

interface Invite {
  email: string;
  note: string | null;
  createdAt: string;
  usedAt: string | null;
}

const PLANS = ['free', 'starter', 'growth', 'enterprise'];
const rate = (n: number, of: number) =>
  of ? `${((n / of) * 100).toFixed(n / of < 0.01 ? 2 : 1)}%` : '—';

/** Vaartix operators only: health of every team, with pause and limit controls. */
export function AdminTeams() {
  const [search, setSearch] = useState('');
  const q = useDebounced(search.trim());
  const overview = useLoad(
    () =>
      api<{
        teams: number;
        pausedTeams: number;
        users: number;
        sentToday: number;
        pendingInvites: number;
      }>('GET', '/admin/overview'),
    [],
  );
  const teams = useLoad(
    () =>
      api<{ data: AdminTeam[] }>(
        'GET',
        `/admin/teams${q ? `?search=${encodeURIComponent(q)}` : ''}`,
      ),
    [q],
  );
  const [editing, setEditing] = useState<{
    team: AdminTeam;
    field: 'limit' | 'plan' | 'pause';
  } | null>(null);
  const toast = useToast();
  const list = teams.data?.data ?? [];

  async function update(team: AdminTeam, body: Record<string, unknown>, message: string) {
    try {
      await api('PATCH', `/admin/teams/${encodeURIComponent(team.id)}`, body);
      toast.show(message);
      teams.reload();
      overview.reload();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not update');
    }
  }

  return (
    <>
      <PageHeader title="Admin" description="Every team on Vaartix. Only you can see this page." />
      <ErrorNote error={teams.error ?? overview.error} />
      {overview.data ? (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="stats" style={{ paddingBottom: 22 }}>
            <div className="stat">
              <div className="label">Teams</div>
              <div className="value">{overview.data.teams}</div>
            </div>
            <div className="stat">
              <div className="label">Users</div>
              <div className="value">{overview.data.users}</div>
            </div>
            <div className="stat">
              <div className="label">Sent today</div>
              <div className="value">{overview.data.sentToday.toLocaleString('en-IN')}</div>
            </div>
            <div className="stat">
              <div className="label">Paused</div>
              <div
                className="value"
                style={{ color: overview.data.pausedTeams ? 'var(--warn)' : undefined }}
              >
                {overview.data.pausedTeams}
              </div>
            </div>
            <div className="stat">
              <div className="label">Invites waiting</div>
              <div className="value">{overview.data.pendingInvites}</div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="filters">
        <div className="search">
          <SearchIcon />
          <input
            className="input"
            type="search"
            placeholder="Search by team name, owner email or team ID…"
            aria-label="Search teams"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Team</th>
              <th>Status</th>
              <th className="hide-sm">Plan</th>
              <th className="nowrap">Sent (7 days)</th>
              <th className="nowrap hide-sm">Bounces</th>
              <th className="nowrap hide-sm">Complaints</th>
              <th className="nowrap hide-sm">Daily limit</th>
              <th style={{ width: 48 }} />
            </tr>
          </thead>
          <tbody>
            {teams.loading && !teams.data ? (
              <SkeletonRows cols={8} rows={4} />
            ) : (
              list.map((team) => {
                const bounceHigh = team.sent >= 100 && team.bounced / team.sent > 0.04;
                const complaintHigh = team.sent >= 100 && team.complained / team.sent > 0.0008;
                return (
                  <tr key={team.id}>
                    <td>
                      <div className="cell-main">
                        <span className="avatar">{team.name.charAt(0).toUpperCase()}</span>
                        <span className="text">
                          <strong>{team.name}</strong>
                          <span className="sub">
                            {team.ownerEmail ?? 'no owner'} · joined {timeAgo(team.createdAt)}
                          </span>
                          <OnboardingLine team={team} />
                        </span>
                      </div>
                    </td>
                    <td title={team.pausedReason ?? undefined}>
                      <Status
                        status={team.status}
                        tone={team.status === 'active' ? 'ok' : 'warn'}
                      />
                      {team.pausedReason ? (
                        <span className="sub">{team.pausedReason}</span>
                      ) : (
                        <span className="sub">{team.reviewedAt ? 'Reviewed' : 'Not reviewed'}</span>
                      )}
                    </td>
                    <td className="hide-sm muted">{team.plan}</td>
                    <td className="nowrap">{team.sent.toLocaleString('en-IN')}</td>
                    <td
                      className="nowrap hide-sm"
                      style={{ color: bounceHigh ? 'var(--bad)' : 'var(--muted)' }}
                    >
                      {team.bounced} <span className="dim">({rate(team.bounced, team.sent)})</span>
                    </td>
                    <td
                      className="nowrap hide-sm"
                      style={{ color: complaintHigh ? 'var(--bad)' : 'var(--muted)' }}
                    >
                      {team.complained}{' '}
                      <span className="dim">({rate(team.complained, team.sent)})</span>
                    </td>
                    <td className="nowrap hide-sm muted">
                      {team.dailyLimit.toLocaleString('en-IN')}
                    </td>
                    <td className="right">
                      <RowMenu
                        items={[
                          team.status === 'active'
                            ? {
                                label: 'Pause sending',
                                danger: true,
                                onSelect: () => setEditing({ team, field: 'pause' }),
                              }
                            : {
                                label: 'Resume sending',
                                onSelect: () =>
                                  void update(team, { status: 'active' }, `${team.name} resumed`),
                              },
                          team.reviewedAt
                            ? {
                                label: 'Mark as not reviewed',
                                onSelect: () =>
                                  void update(
                                    team,
                                    { reviewed: false },
                                    `${team.name} marked as not reviewed`,
                                  ),
                              }
                            : {
                                label: 'Mark as reviewed',
                                onSelect: () =>
                                  void update(
                                    team,
                                    { reviewed: true },
                                    `${team.name} reviewed: content checks lifted`,
                                  ),
                              },
                          {
                            label: 'Change daily limit',
                            onSelect: () => setEditing({ team, field: 'limit' }),
                          },
                          {
                            label: 'Change plan',
                            onSelect: () => setEditing({ team, field: 'plan' }),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <div className="table-foot">
        <span>
          {list.length} team{list.length === 1 ? '' : 's'}
        </span>
      </div>

      {editing ? (
        <EditTeam
          team={editing.team}
          field={editing.field}
          onClose={() => setEditing(null)}
          onSave={(body, message) => {
            const team = editing.team;
            setEditing(null);
            void update(team, body, message);
          }}
        />
      ) : null}
      {toast.node}
    </>
  );
}

function EditTeam({
  team,
  field,
  onClose,
  onSave,
}: {
  team: AdminTeam;
  field: 'limit' | 'plan' | 'pause';
  onClose: () => void;
  onSave: (body: Record<string, unknown>, message: string) => void;
}) {
  const [limit, setLimit] = useState(String(team.dailyLimit));
  const [plan, setPlan] = useState(team.plan);
  const [reason, setReason] = useState('');

  function submit(e?: FormEvent) {
    e?.preventDefault();
    if (field === 'limit')
      onSave(
        { dailyLimit: Number(limit) },
        `Daily limit set to ${Number(limit).toLocaleString('en-IN')}`,
      );
    if (field === 'plan') onSave({ plan }, `Plan changed to ${plan}`);
    if (field === 'pause') onSave({ status: 'paused', reason }, `${team.name} paused`);
  }

  const titles = {
    limit: `Daily limit for ${team.name}`,
    plan: `Plan for ${team.name}`,
    pause: `Pause sending for ${team.name}?`,
  };
  return (
    <Dialog
      title={titles[field]}
      description={
        field === 'pause'
          ? 'Their API calls to send are refused until you resume them. They see your reason in the dashboard.'
          : undefined
      }
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            form="edit-team"
            className={field === 'pause' ? 'btn btn-danger' : 'btn btn-primary'}
          >
            {field === 'pause' ? 'Pause sending' : 'Save'}
          </button>
        </>
      }
    >
      <form id="edit-team" onSubmit={submit} style={{ display: 'contents' }}>
        {field === 'limit' ? (
          <div className="field">
            <label htmlFor="limit">Recipients per day</label>
            <input
              id="limit"
              className="input"
              inputMode="numeric"
              value={limit}
              onChange={(e) => setLimit(e.target.value.replace(/\D/g, ''))}
            />
            <span className="hint">
              Currently {team.dailyLimit.toLocaleString('en-IN')}. Resets at midnight IST.
            </span>
          </div>
        ) : null}
        {field === 'plan' ? (
          <div className="field">
            <label htmlFor="plan">Plan</label>
            <select
              id="plan"
              className="input"
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
            >
              {PLANS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <span className="hint">
              The plan sets how many domains and team members they can have.
            </span>
          </div>
        ) : null}
        {field === 'pause' ? (
          <div className="field">
            <label htmlFor="reason">Reason (shown to the team)</label>
            <input
              id="reason"
              className="input"
              placeholder="For example: high bounce rate, please clean your list"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        ) : null}
      </form>
    </Dialog>
  );
}

/** Vaartix operators only: who may sign up during the private beta. */
export function AdminInvites() {
  const invites = useLoad(() => api<{ data: Invite[] }>('GET', '/admin/invites'), []);
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const toast = useToast();
  const list = invites.data?.data ?? [];

  async function invite(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('POST', '/admin/invites', {
        email: email.trim(),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      toast.show(`${email.trim()} invited. A welcome email is on its way.`);
      setEmail('');
      setNote('');
      invites.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Invites"
        description="During the beta, only invited emails can create an account."
      />
      <div className="card card-body" style={{ marginBottom: 16 }}>
        <form
          onSubmit={invite}
          style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}
        >
          <div className="field" style={{ flex: '2 1 240px' }}>
            <label htmlFor="invite-email">Email</label>
            <input
              id="invite-email"
              className="input"
              type="email"
              placeholder="founder@startup.in"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="field" style={{ flex: '3 1 240px' }}>
            <label htmlFor="invite-note">Note (only you see it)</label>
            <input
              id="invite-note"
              className="input"
              placeholder="OTP emails, ~20k/month, from the early-access form"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={busy || !email.trim()}>
            <PlusIcon />
            {busy ? 'Inviting…' : 'Invite'}
          </button>
        </form>
        <ErrorNote error={error} />
      </div>

      {!invites.loading && list.length === 0 ? (
        <Empty icon={<ShieldIcon />} title="No invites yet">
          <p>
            Invite people from your early-access list. They get a welcome email and can log in
            straight away.
          </p>
        </Empty>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Status</th>
                <th className="hide-sm">Note</th>
                <th className="nowrap hide-sm">Invited</th>
                <th style={{ width: 48 }} />
              </tr>
            </thead>
            <tbody>
              {invites.loading && !invites.data ? (
                <SkeletonRows cols={5} rows={3} />
              ) : (
                list.map((inv) => (
                  <tr key={inv.email}>
                    <td>
                      <div className="cell-main">
                        <span className={`row-icon ${inv.usedAt ? 'ok' : ''}`}>
                          <UsersIcon />
                        </span>
                        <strong>{inv.email}</strong>
                      </div>
                    </td>
                    <td>
                      {inv.usedAt ? (
                        <Status status="Joined" tone="ok" />
                      ) : (
                        <Status status="Invited" tone="info" />
                      )}
                      {inv.usedAt ? <span className="sub">{formatDate(inv.usedAt)}</span> : null}
                    </td>
                    <td className="hide-sm muted truncate">{inv.note ?? '—'}</td>
                    <td className="nowrap hide-sm muted">{timeAgo(inv.createdAt)}</td>
                    <td className="right">
                      {inv.usedAt ? null : (
                        <RowMenu
                          items={[
                            {
                              label: 'Cancel invite',
                              danger: true,
                              onSelect: async () => {
                                await api(
                                  'DELETE',
                                  `/admin/invites/${encodeURIComponent(inv.email)}`,
                                );
                                toast.show('Invite cancelled');
                                invites.reload();
                              },
                            },
                          ]}
                        />
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      {toast.node}
    </>
  );
}
